<template>
  <v-form
    ref="form"
    style="overflow: scroll;"
  >
    <v-container
      grid-list-xl
      fluid
    >
      <v-layout
        row
        wrap
      >
        <v-flex
          xs12
          sm6
        >
          <v-layout row>
            <v-flex>
              <v-text-field
                v-model="invoice.invoiceNumber"
                :rules="[v => !!v || 'required']"
                label="Invoice Number"
                :disabled="autogenerate"
                required
              />
            </v-flex>
            <v-flex>
              <v-checkbox
                v-model="autogenerate"
                label="Auto Generate"
                @change="generateInvoiceNumber"
              />
            </v-flex>
          </v-layout>
          <v-menu
            v-model="invoiceDateMenu"
            :close-on-content-click="false"
            lazy
            transition="scale-transition"
            full-width
            min-width="290px"
          >
            <v-text-field
              slot="activator"
              :value="formatDate(invoiceDate)"
              :rules="[v => !!v || 'required']"
              label="Invoice Date"
              append-icon="event"
              readonly
              required
            />
            <v-date-picker
              v-model="invoiceDate"
              @input="invoiceDateMenu = false"
            />
          </v-menu>
          <v-layout
            row
            align-center
          >
            <v-flex class="pl-0">
              <v-list-tile dense>
                <v-list-tile-content>
                  <v-list-tile-title>Terms</v-list-tile-title>
                  <v-list-tile-sub-title>Invoice due in days</v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-flex>
            <v-flex xs5> 
              <v-select
                v-model="invoice.terms"
                :items="Object.keys(terms)"
                :rules="[v => !!v || 'required']"
                required
                @change="updateDueDate"
              />
            </v-flex>
          </v-layout>
          <v-menu
            v-model="dueDateMenu"
            :close-on-content-click="false"
            lazy
            transition="scale-transition"
            full-width
            min-width="290px"
          >
            <v-text-field
              slot="activator"
              :value="formatDate(dueDate)"
              :rules="[v => !!v || 'required']"
              label="Due Date"
              append-icon="event"
              readonly
              required
            />
            <v-date-picker
              v-model="dueDate"
              @input="dueDateMenu = false"
            />
          </v-menu>
        </v-flex>
        <v-flex
          xs12
          sm6
          class="px-5"
        >
          <v-subheader>Billing Address</v-subheader>
          <div class="subheading font-weight-medium">
            {{ invoice.companyName }}
          </div>
          <div class="body-2">
            {{ invoice.address.line1 }}
          </div>
          <div class="body-2">
            {{ invoice.address.line2 }}
          </div>
          <div class="body-2">
            {{ invoice.address.city }}
          </div>
          <div class="body-2">
            {{ invoice.address.state }}
          </div>
          <div class="body-2">
            {{ invoice.address.pincode }}
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    
    <v-container grid-list-lg>
      <v-subheader>Items</v-subheader>


      <v-layout
        row
        wrap
        class="grey darken-1 white--text font-weight-bold"
      >
        <v-flex
          xs6
        >
          Item & Description
        </v-flex>
        <v-flex
          xs1
          text-xs-center
        >
          Qty
        </v-flex>
        <v-flex
          xs2
          text-xs-center
        >
          Rate
        </v-flex>
        <v-flex
          xs2
          text-xs-center
        >
          Amount
        </v-flex>
      </v-layout>


      <template v-for="(item, index) in invoice.items">
        <v-layout
          :key="index"
          row
          align-center
        >
          <v-flex xs6>
            <v-text-field
              v-model="item.name"
              hide-details
              label="Item"
              :rules="[v => !!v || 'required']"
              full-width
              required
            />
            <v-text-field
              v-model="item.description"
              hide-details
              label="Description"
              :rules="[v => !!v || 'required']"
              full-width
              required
            />
          </v-flex>
          <v-flex xs1>
            <v-text-field
              v-model.number="item.qty"
              type="number"
              hide-details
              label="Qty"
              :rules="[v => !!v || 'required']"
              reverse
              required
              @change="item.amount = item.qty * item.rate"
            />
          </v-flex>
          <v-flex xs2>
            <v-text-field
              v-model.number="item.rate"
              type="number"
              hide-details
              label="Rate"
              :rules="[v => !!v || 'required']"
              reverse
              required
              @change="item.amount = item.qty * item.rate"
            />
          </v-flex>
          <v-flex xs2>
            <v-text-field
              :value="formatNumber(item.amount)"
              hide-details
              reverse
              readonly
              label="Amount"
            />
          </v-flex>
          <v-flex xs1>
            <v-btn
              flat
              icon
              color="warning"
              :hidden="!index"
              @click="removeInvoiceItem(index)"
            >
              <v-icon>
                mdi-close-circle-outline
              </v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
        <v-divider
          :key="'divider'+index"
          class="px-2"
        />
      </template>
      
      <v-divider />


      <v-layout row>
        <v-btn
          flat
          color="primary"
          @click="addInvoiceItem"
        >
          Add Item
        </v-btn>
      </v-layout>


      <v-layout row>
        <v-flex xs7 />
        <v-flex xs2>
          <div class="subheading">
            Sub Total
          </div>
        </v-flex>
        <v-flex
          xs2
          text-xs-right
        >
          <div class="subheading">
            {{ formatNumber(subTotal) }}
          </div>
        </v-flex>
        <v-flex xs1 />
      </v-layout>
      

      <v-layout
        row
        align-center
      >
        <v-flex xs7 />
        <v-flex xs2>
          <div class="subheading">
            Tax Rate
          </div>
        </v-flex>
        <v-flex
          xs1
          text-xs-right
        >
          <v-select
            v-model="invoice.tax"
            :items="taxRates"
          />
        </v-flex>
        <v-flex xs1 />
      </v-layout>
      

      <v-layout row>
        <v-flex xs7 />
        <v-flex xs2>
          <div class="subheading">
            CGST
          </div>
        </v-flex>
        <v-flex
          xs2
          text-xs-right
        >
          <div class="subheading">
            {{ formatNumber(subTotal * invoice.tax/200) }}
          </div>
        </v-flex>
        <v-flex xs1 />
      </v-layout>
      

      <v-layout row>
        <v-flex xs7 />
        <v-flex xs2>
          <div class="subheading">
            SGST
          </div>
        </v-flex>
        <v-flex
          xs2
          text-xs-right
        >
          <div class="subheading">
            {{ formatNumber(subTotal * invoice.tax/200) }}
          </div>
        </v-flex>
        <v-flex xs1 />
      </v-layout>
      

      <v-layout row>
        <v-flex xs7 />
        <v-flex
          xs2
          class="blue-grey lighten-4 font-weight-bold"
        >
          Total
        </v-flex>
        <v-flex
          xs2
          text-xs-right
          class="blue-grey lighten-4 font-weight-bold"
        >
          {{ formatNumber(total) }}
        </v-flex>
        <v-flex xs1 />
      </v-layout>

      <v-layout
        row
        class="py-3"
      >
        <v-flex
          xs10
          sm7
          md5
        >
          <v-textarea
            v-model="invoice.customerNotes"
            label="Customer Notes"
            hint="will be displayed on invoice"
            persistent-hint
            outline
          />
          <v-textarea
            v-model="invoice.tnc"
            label="Terms & Conditions"
            hint="will be displayed on invoice"
            persistent-hint
            outline
          />
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import moment from 'moment';
import firebase, { database, firestore } from '@/firebase';

export default {
  name: 'NewInvoice',
  props: {
    tenant: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      invoice: {
        invoiceNumber: null,
        invoiceDate: moment().toDate(),
        dueDate: null,
        terms: 'Net15',
        companyName: null,
        GSTIN: null,
        address: {},
        items: [],
        tax: 18,
        customerNotes: null,
        tnc: null,
        status: 'DRAFT'
      },
      invoiceItem: {
        name: null,
        description: null,
        qty: 1,
        rate: 0,
        tax: null,
        amount: 0
      },
      terms: {
        Net5: 5,
        Net10: 10,
        Net15: 15,
        Net30: 30,
        'Due on Receipt': 0
      },
      taxRates: [
        { text: '0%', value: 0 },
        { text: '5%', value: 5 },
        { text: '12%', value: 12 },
        { text: '18%', value: 18 },
        { text: '28%', value: 28 }
      ],
      invoiceDateMenu: false,
      dueDateMenu: false,
      autogenerate: false
    };
  },
  computed: {
    invoiceDate: {
      get() {
        return moment(this.invoice.invoiceDate.toISOString()).format(
          'YYYY-MM-DD'
        );
      },
      set(val) {
        this.invoice.invoiceDate = moment(val).toDate();
      }
    },

    dueDate: {
      get() {
        return moment(this.invoice.dueDate).format('YYYY-MM-DD');
      },
      set(val) {
        this.invoice.dueDate = moment(val).toDate();
      }
    },

    subTotal() {
      return this.invoice.items.reduce(
        (subTotal, item) => subTotal + item.amount,
        0
      );
    },

    total() {
      const total = this.subTotal + (this.subTotal * this.invoice.tax) / 100;
      return Math.round(total);
    }
  },
  mounted() {
    //set tenant details
    this.invoice.companyName = this.tenant.companyName;
    this.invoice.address = this.tenant.billingSettings.address;
    this.invoice.GSTIN = this.tenant.billingSettings.GSTIN;

    this.updateDueDate(this.invoice.terms);
    if (this.invoice.items.length < 1) this.addInvoiceItem();
    this.generateInvoiceNumber();

    //load invoice settings
    firestore
      .doc('settings/maxTowers')
      .get()
      .then(settingsSnapshot => {
        const settings = settingsSnapshot.get('invoiceSettings');
        this.invoice.customerNotes = settings.customerNotes;
        this.invoice.tnc = settings.tnc;
      });
  },
  methods: {
    generateInvoiceNumber() {
      if (this.autogenerate) {
        database
          .ref('settings/invoices')
          .transaction(currentData => {
            if (currentData) {
              currentData.nextNumber += 1;
            }
            return currentData;
          })
          .then(result => {
            if (result.committed) {
              const settings = result.snapshot.val();
              this.invoice.invoiceNumber =
                settings.prefix + '-' + settings.nextNumber;
            }
          });
      }
    },

    addInvoiceItem() {
      this.invoice.items.push(Object.assign({}, this.invoiceItem));
    },

    removeInvoiceItem(index) {
      this.invoice.items.splice(index, 1);
    },

    formatNumber(val) {
      return val.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        currency: 'INR'
      });
    },

    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    },

    updateDueDate(val) {
      this.dueDate = moment(this.invoiceDate)
        .add(this.terms[val], 'days')
        .format('YYYY-MM-DD');
    },

    validate() {
      //set invoice total to computed total
      this.invoice.total = this.total;

      //add created date
      this.invoice.createdDate = firebase.firestore.FieldValue.serverTimestamp();

      if (this.$refs.form.validate()) {
        return this.invoice;
      }

      return null;
    }
  }
};
</script>
